import React, { useEffect, useRef, useState } from 'react'

import { useParams, useNavigate } from 'react-router-dom'

import { UpdateZoneRequestDTO } from '@/core/api/dtos/request/zone/update'
import permissionService, {
	PERMISSIONS,
} from '@/core/api/services/permissionService'
import { isAccessDeniedError } from '@/core/api/utils/httpErrors'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useLocation } from '@/core/hooks/useLocation'
import { usePermission } from '@/core/hooks/usePermission'
import { useUpdateZone } from '@/core/hooks/useZone'
import { useTranslation } from '@/core/i18n/useTranslation'
import { useLocationShare } from '@/features/zones/hooks/useLocationShare'
import { useLocationUpdate } from '@/features/zones/hooks/useLocationUpdate'
import useZoneDeletion from '@/features/zones/hooks/useZoneDeletion'
import useZoneEdit from '@/features/zones/hooks/useZoneEdit'
import useZoneRefresh from '@/features/zones/hooks/useZoneRefresh'
import { useZoneSendCodeEmail } from '@/features/zones/hooks/useZoneSendCodeEmail'
import DeleteZoneModal from '@/features/zones/modals/DeleteZoneModal'
import EditZoneModal from '@/features/zones/modals/EditZoneModal'
import RefreshZoneModal from '@/features/zones/modals/RefreshZoneModal'
import SendCodeEmailZoneModal from '@/features/zones/modals/SendCodeEmailZoneModal'
import ShareLocationModal from '@/features/zones/modals/ShareLocationModal'
import UpdateLocationModal from '@/features/zones/modals/UpdateLocationModal'
import { Button } from '@/shared/components/atoms/Button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

import LocationDetailsUI from './LocationDetailsUI'

const LocationDetails: React.FC = () => {
	// Get the locationId from the URL params
	const { locationId } = useParams<{ locationId: string }>()

	// Get the navigate function
	const navigate = useNavigate()

	const [accessDeniedModalOpen, setAccessDeniedModalOpen] = useState(false)
	const redirectToRef = useRef<string>('/zones?tab=locations')
	const { showNotification } = useNotificationContext()
	const { t: tCommon } = useTranslation('common')
	const currentAccount = useCurrentAccount()
	const { features } = usePermission()

	const canEditLocation =
		permissionService.currentUserHasAnyPermission([
			PERMISSIONS.MANAGE_CHILDREN_PLAYERS,
			PERMISSIONS.MANAGE_OWN_LEVEL_PLAYERS,
			PERMISSIONS.UPDATE_USER_STORES,
		]) && features.location.edit

	// Redirect to zones list if locationId is not a number
	if (Number.isNaN(Number(locationId))) {
		navigate('/zones')
	}

	// Fetch location data (include zones/players with full data for zone cards)
	const {
		data: location,
		isLoading,
		error,
		refetch,
	} = useLocation({
		playerId: Number(locationId),
		config: useAccountConfig({
			params: {
				include:
					'players.state,players.box_allocations,players.groups.schedules,players.last_played_song,players.last_played_schedule,lastGooglePlaceData,account',
			},
		}),
		options: {
			enabled: !!locationId && !Number.isNaN(Number(locationId)),
		},
	})

	// Location update hook
	const locationUpdate = useLocationUpdate(() => {
		refetch() // Refetch location after successful update
	})

	// Location sharing hook
	const locationShare = useLocationShare({
		onSuccess: () => {
			refetch() // Refetch location after successful sharing
		},
		locationId: Number(locationId),
	})

	// Zone deletion hook
	const zoneDeletion = useZoneDeletion({
		onSuccess: () => refetch(),
	})

	// Zone refresh hook
	const zoneRefresh = useZoneRefresh({
		onSuccess: () => refetch(),
	})

	const zoneSendCodeEmail = useZoneSendCodeEmail()

	// Zone edit hook
	const zoneEdit = useZoneEdit({
		onSuccess: () => refetch(),
	})

	// Zone update mutation
	const { mutate: updateZone, isPending: isUpdating } = useUpdateZone({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				showNotification({
					type: 'success',
					message: tCommon('entities.zone.edit.success.title'),
					description: tCommon('entities.zone.edit.success.description'),
				})
				refetch()
			},
			onError: () => {
				showNotification({
					type: 'error',
					message: tCommon('entities.zone.edit.error.title'),
					description: tCommon('common.try_again_later'),
				})
			},
		},
	})

	const isTrialAccount =
		(currentAccount?.getTrialSubscriptions()?.length ?? 0) > 0 &&
		!!currentAccount?.source

	// Handle back navigation
	const handleBack = () => {
		navigate('/zones?tab=locations')
	}

	// Warn before redirecting on 403/404 errors for location query
	useEffect(() => {
		if (!error) return
		if (!isAccessDeniedError(error)) return
		redirectToRef.current = '/zones?tab=locations'
		setAccessDeniedModalOpen(true)
	}, [error])

	// Handle change information button
	const handleChangeInformation = () => {
		if (location) {
			locationUpdate.openUpdateModal(location)
		}
	}

	// Handle share access button
	const handleShareAccess = () => {
		if (location) {
			locationShare.openShareModal(location)
		}
	}

	const handleUpdateZone = (zoneId: number) => (data: UpdateZoneRequestDTO) => {
		updateZone({ zoneId, data })
	}

	const handleAddZone = () => {
		navigate('/zones/create')
	}

	return (
		<>
			<Dialog
				open={accessDeniedModalOpen}
				onOpenChange={(open) => {
					if (!open) {
						setAccessDeniedModalOpen(false)
						navigate(redirectToRef.current, { replace: true })
						return
					}
					setAccessDeniedModalOpen(true)
				}}
			>
				<DialogContent className="sm:max-w-[520px]">
					<DialogHeader>
						<DialogTitle>{tCommon('locations.accessDenied.title')}</DialogTitle>
						<DialogDescription>
							{tCommon('locations.accessDenied.description')}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => window.open('https://wa.me/15557052438', '_blank')}
						>
							{tCommon('actions.contactSupport')}
						</Button>
						<Button
							onClick={() => {
								setAccessDeniedModalOpen(false)
								navigate(redirectToRef.current, { replace: true })
							}}
						>
							{tCommon('actions.close')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<LocationDetailsUI
				location={location}
				isLoading={isLoading}
				error={error}
				onBack={handleBack}
				onChangeInformation={handleChangeInformation}
				canEditLocation={canEditLocation}
				onShareAccess={handleShareAccess}
				zones={location?.zones ?? []}
				onZoneClick={(zone) => navigate(`/zones/${zone.id}`)}
				openDeleteModal={zoneDeletion.openDeleteModal}
				openRefreshModal={zoneRefresh.openRefreshModal}
				openEditModal={zoneEdit.openEditModal}
				openSendCodeEmailModal={zoneSendCodeEmail.openSendCodeEmailModal}
				handleAddZone={handleAddZone}
				handleConnectZone={(zone) => navigate(`/zones/${zone.id}`)}
				handleUpdateZone={handleUpdateZone}
				isUpdating={isUpdating}
				isTrialAccount={!!isTrialAccount}
			/>

			{/* Update Location Modal */}
			<UpdateLocationModal
				visible={locationUpdate.isModalVisible}
				location={locationUpdate.selectedLocation}
				onClose={locationUpdate.closeUpdateModal}
				onConfirm={locationUpdate.handleUpdateLocation}
				isUpdating={locationUpdate.isUpdating}
			/>

			{/* Share Location Modal */}
			<ShareLocationModal
				visible={locationShare.isModalVisible}
				currentStep={locationShare.currentStep}
				contactData={locationShare.originalContactData}
				isUpdating={locationShare.isUpdating}
				isRegenerating={locationShare.isRegenerating}
				isSharing={locationShare.isSharing}
				onClose={locationShare.closeShareModal}
				onNextStep={locationShare.nextStep}
				onRegenerateLink={locationShare.handleRegenerateLink}
				onCopyLink={locationShare.handleCopyLink}
				onShareLink={locationShare.handleShareLink}
			/>

			<DeleteZoneModal
				visible={zoneDeletion.isModalVisible}
				zone={zoneDeletion.selectedZone}
				onClose={zoneDeletion.closeDeleteModal}
				onConfirm={zoneDeletion.handleDeleteZone}
				isDeleting={zoneDeletion.isDeleting}
				onPage="zones"
			/>

			<RefreshZoneModal
				visible={zoneRefresh.isModalVisible}
				onClose={zoneRefresh.closeRefreshModal}
				onConfirm={zoneRefresh.handleRefreshZone}
				isRefreshing={zoneRefresh.isRefreshing}
			/>

			<SendCodeEmailZoneModal
				visible={zoneSendCodeEmail.isModalVisible}
				zone={zoneSendCodeEmail.selectedZone}
				onClose={zoneSendCodeEmail.closeSendCodeEmailModal}
				onConfirm={zoneSendCodeEmail.handleSendCodeEmail}
				isSending={zoneSendCodeEmail.isSending}
			/>

			<EditZoneModal
				visible={zoneEdit.isModalVisible}
				zone={zoneEdit.selectedZone}
				onClose={zoneEdit.closeEditModal}
				onSubmit={zoneEdit.handleEditZone}
				isLoading={zoneEdit.isEditing}
			/>
		</>
	)
}

export default LocationDetails
